// @flow

import _ from 'lodash';

/*
USUARIOS EXTERNOS:

Guest                   Usuario no autentificado sin datos ni privilegios.

Unknown                 Este usuario está autentificado, pero no se sabe de qué tipo es.
                        Hay que darle alguno de los siguientes roles:

Lab                     Usuario que pertenece a un laboratorio y que sube muestras de suelo.

Agricultor              Dueño de un campo. Sólo ve sus carpetas.
                        Excepto por las observaciones y archivos propios, este usuario es read only.

AsesorExterno           Administra un conjunto de predios y sus agricultores asociados.
                        No puede subir ni borrar información.

USUARIOS INTERNOS:

CoordinadorExterno      Igual que un coordinador interno, pero NO PUEDE CAMBIAR SUS DIRECTORIOS

MiembroAgrosat          Este usuario pertenece a agrosat, pero no se sabe de qué tipo es.
                        Hay que darle alguno de los siguientes roles:

ServiceManager          Puede subir información. Puede cambiar sus directorios.
                        No puede cambiar usuarios

Coordinador             Debe estar asociado a uno o más Programas de desarrollo. Puede cambiar,
                        crear y borrar usuarios que estén en esos programas de desarrollo.
                        No puede subir archivos shape o imágenes.

Gerencia                No puede subir archivos. Puede administrar usuarios.

ProduccionUpload        Puede subir información.

Produccion              Puede subir y administrar información.

ProduccionPro           Puede subir y borrar información, crear carpetas y administrar usuarios.

Informatica             Informática. Puede hacer todo.

WebGoddess              Igual que informática, pero mucho más cool.
*/


export const Guest:              'Guest'              = 'Guest';
export const Unknown:            'Unknown'            = 'Unknown';
export const Lab:                'Lab'                = 'Lab';
export const Agricultor:         'Agricultor'         = 'Agricultor';
export const AsesorExterno:      'AsesorExterno'      = 'AsesorExterno';
export const CoordinadorExterno: 'CoordinadorExterno' = 'CoordinadorExterno';
export const MiembroAgrosat:     'MiembroAgrosat'     = 'MiembroAgrosat';
export const ServiceManager:     'ServiceManager'     = 'ServiceManager';
export const Coordinador:        'Coordinador'        = 'Coordinador';
export const Gerencia:           'Gerencia'           = 'Gerencia';
export const ProduccionUpload:   'ProduccionUpload'   = 'ProduccionUpload';
export const Produccion:         'Produccion'         = 'Produccion';
export const ProduccionPro:      'ProduccionPro'      = 'ProduccionPro';
export const Informatica:        'Informatica'        = 'Informatica';
export const WebGoddess:         'WebGoddess'         = 'WebGoddess';


export type T_Role =
  typeof Guest              |
  typeof Unknown            |
  typeof Lab                |
  typeof Agricultor         |
  typeof AsesorExterno      |
  typeof CoordinadorExterno |
  typeof MiembroAgrosat     |
  typeof ServiceManager     |
  typeof Coordinador        |
  typeof Gerencia           |
  typeof ProduccionUpload   |
  typeof Produccion         |
  typeof ProduccionPro      |
  typeof Informatica        |
  typeof WebGoddess         ;



export default class Role {
  static externos: Array<T_Role> = [
    Guest         ,
    Lab           ,
    Agricultor    ,
    AsesorExterno 
  ];
  
  static internos: Array<T_Role> = [
    MiembroAgrosat     ,
    ServiceManager     ,
    ProduccionUpload   ,
    Produccion         ,
    ProduccionPro      ,
    Gerencia           ,
    Informatica        ,
    WebGoddess         ,  
    // CoordinadorExterno ,  // DESHABILITADO POR PROBLEMAS DE CONFIANZA hasta 27-01-2020
    // Coordinador           // DESHABILITADO POR PROBLEMAS DE CONFIANZA hasta 27-01-2020
  ];
  
  static known: Array<T_Role> = [
    Agricultor         ,
    AsesorExterno      ,     // ahora es un rol conocido.
    CoordinadorExterno ,
    // ServiceManager  ,
    Coordinador        ,
    Produccion         ,
    ProduccionUpload   ,
    ProduccionPro      ,
    Gerencia           ,
    Informatica        ,
    WebGoddess
  ];
  
  static admin_users: Array<T_Role> = [
    // CoordinadorExterno,  // DESHABILITADO POR PROBLEMAS DE CONFIANZA
    Coordinador       ,     // ESTE PERFIL SOLO PUEDE CREAR USUARIOS QUE VEAN LAS MISMAS CARPETAS ASIGNADAS A ELLOS.
    ProduccionPro     ,
    Gerencia          ,
    Informatica       ,
    WebGoddess
  ];
  
  static admin_info: Array<T_Role> = [
    ServiceManager ,
    Produccion     ,
    ProduccionPro  ,
    Gerencia       ,
    Informatica    ,
    WebGoddess
    // Coordinador ,    // DESHABILITADO POR PROBLEMAS DE CONFIANZA
  ];
  
  static admin_escalas: Array<T_Role> = [
    Produccion     ,
    ProduccionPro  ,
    WebGoddess
  ];
  
  static reports: Array<T_Role> = [
    Coordinador        ,
    CoordinadorExterno ,
    ProduccionPro      ,
    Gerencia           ,
    Informatica        ,
    WebGoddess
  ];
  
  
  
  static coordinador_externo = CoordinadorExterno;
  static asesor_externo      = AsesorExterno;
  static coordinador         = Coordinador;
  static agricultor          = Agricultor;
  static web_goddess         = WebGoddess;
  
  
  
  static is_agrosat_user(role: T_Role): boolean {
    return _.includes(Role.internos, role);
  }
  
  
  
  static is_externo(role: T_Role): boolean {
    return ! Role.is_agrosat_user(role);
  }
  
  
  
  static can_admin_info(role: T_Role): boolean {
    return Role.is_agrosat_user(role) && _.includes(Role.admin_info, role);
  }
  
  
  
  static can_admin_users(role: T_Role): boolean {
    return (Role.is_agrosat_user(role) && _.includes(Role.admin_users, role)) || role === Coordinador; // le añadi esta comparacion
  }
  
  
  
  static can_admin_escalas(role: T_Role): boolean {
    return Role.is_agrosat_user(role) && _.includes(Role.admin_escalas, role);
  }
  
  
  
  static es_produccion(role: T_Role): boolean {
    return Role.is_agrosat_user(role) && _.includes([Produccion, ProduccionUpload, ProduccionPro], role);
  }
  
  
  
  static can_view_reports(role: T_Role): boolean {
    return Role.is_agrosat_user(role) && _.includes(Role.reports, role);
  }
  
  
  
  static status(role: T_Role): number {
    if (role === Coordinador) {             // NOTE: si bien el Coordinador no es un usuario interno,
      return 0.5;                           // puede administrar sus usuarios. Por tanto, su status
    }                                       // debe ser mayor que 0.
    
    const index = Role.internos.indexOf(role);
    
    if (role === Guest)        { return -1; }
    if (Role.is_externo(role)) { return  0; }
    if (index === -1)          { return -1; }
    
    return index + 1;
  }
  
  
  
  // Devuelve qué tipos de usuarios pueden crear los roles de admin_users
  
  static can_create(role: T_Role): Array<T_Role> {
    if (! Role.can_admin_users(role)) {
      return [];
    }
    const status = Role.status(role);
    
    return [Agricultor, AsesorExterno, CoordinadorExterno, Coordinador].concat(_.filter(_.drop(Role.internos), (rol) => {
      return Role.status(rol) < status;
    }));
  }
  
  
  
  static can_change(role: T_Role): Array<T_Role> {
    if (! Role.can_admin_users(role)) {
      return [];
    }
    if (role === CoordinadorExterno) {
      const rs = Role.can_create(role);        // ++ rol Unknown, para que los coordinadores externos puedan
      rs.unshift(Unknown);                   // asignarle un rol válido.
      return rs;
    }
    const status = Role.status(role);
    
    return [Unknown, Agricultor, AsesorExterno].concat(_.filter(Role.internos, (rol) => {
      return Role.status(rol) < status;
    }));
  }
  
  
  
  static friendly_name(role: T_Role): string {
    if (role === WebGoddess) {
      return 'Administrador';
    }
    else if (role === Unknown) {
      return 'Desconocido';
    }
    else {
      const str: string = (role: string);
      
      return _.words(str).join(' ');
    }
  }
}

// NOTE: no se te ocurra hacer un push que incluya un window.Role = Role;
// node no tiene un objeto window, así que el docker no va a arrancar.





// WEBPACK FOOTER //
// ./src/roles.js